import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import OrderSheet from './OrderSheet';
import orderSheetService, { type OrderSheetResponse } from '../../services/orderSheetService';
import purchaseOrderService from '../../services/purchaseOrderService';

/**
 * PO-SHEET.1c — DOOR B, for real this time.
 *
 * The alerts workspace's review step used to ask "how many?" beside a bare
 * item name, hiding the two numbers that answer the question: what is on the
 * shelf, and what the par is. §7 of the design called that its known gap.
 *
 * This renders the SAME <OrderSheet> the supplier-first door renders — not a
 * lookalike, not a shared prop shape. PO-SHEET.1 claimed both doors landed on
 * one component and they did not; the claim went untested because the test
 * rendered the component directly instead of walking the page. The tests
 * beside this one now click through both real pages and assert on the same
 * component identity.
 *
 * The alert selection narrows the sheet: `itemIds` is what the workspace
 * picked, so the reviewer sees exactly the items they chose, with the columns
 * they were missing.
 */

export interface AlertsReorderSheetProps {
  venueId: string;
  supplierId: string;
  supplierName: string;
  /** The alert selection for this supplier group. */
  itemIds: string[];
  /** Bubble the created PO id so the group can show its done state. */
  onCreated: (purchaseOrderId: string) => void;
}

const AlertsReorderSheet: React.FC<AlertsReorderSheetProps> = ({
  venueId, supplierId, supplierName, itemIds, onCreated,
}) => {
  const [sheet, setSheet] = useState<OrderSheetResponse | null>(null);
  const [query, setQuery] = useState('');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      setSheet(await orderSheetService.get(venueId, supplierId, { q, itemIds }));
    } catch {
      setError(`Could not load the order sheet for ${supplierName}.`);
    } finally {
      setLoading(false);
    }
    // itemIds is a fresh array each render upstream; key on its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, supplierId, supplierName, itemIds.join(',')]);

  useEffect(() => {
    const t = setTimeout(() => { load(query); }, query ? 300 : 0);
    return () => clearTimeout(t);
  }, [query, load]);

  /**
   * THE LAW at the boundary, identical to door A: only filled lines become PO
   * lines. An untouched line is not sent with a zero — it is not sent.
   */
  const create = async () => {
    if (!sheet) return;
    const lines = sheet.lines
      .map(l => ({ id: l.inventoryItemId, qty: quantities[l.inventoryItemId] ?? 0 }))
      .filter(l => l.qty > 0);
    if (lines.length === 0) return;

    setCreating(true);
    setError(null);
    try {
      const po = await purchaseOrderService.createOrder(venueId, { supplierId });
      for (const l of lines) {
        await purchaseOrderService.createLine(venueId, po._id, {
          inventoryItemId: l.id,
          orderedQty: l.qty,
        });
      }
      onCreated(po._id);
    } catch {
      // The sheet and its quantities survive a failure — the reviewer's work
      // is not thrown away because a request did not land.
      setError('The sheet is intact — creating the draft failed. Try again.');
    } finally {
      setCreating(false);
    }
  };

  if (loading && !sheet) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box data-testid="alerts-reorder-sheet">
      {error && <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {sheet && (
        <OrderSheet
          sheet={sheet}
          loading={loading}
          query={query}
          onQueryChange={setQuery}
          quantities={quantities}
          onQuantitiesChange={setQuantities}
          onCreate={create}
          creating={creating}
        />
      )}
    </Box>
  );
};

export default AlertsReorderSheet;
